import { Tooltip } from 'antd';
import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { useIntercom } from 'react-use-intercom';
import styled, { css } from 'styled-components';

import { ArrowLeftSvg, PlusSvg, QuestionMarkSvg } from '../../../assets/icons';
import ArrowRightSvg from '../../../assets/icons/ArrowRightSvg';
import HomeFilledSvg from '../../../assets/icons/HomeFilledSvg';
import { MagicBackground } from '../../../assets/icons/MagicBackground';
import { SlimLeftArrow } from '../../../assets/icons/SlimLeftArrow';
import theme from '../../../assets/theme';
import useAccessBadge from '../../../hooks/useAccessBadge';
import { useBreakPoint } from '../../../hooks/useBreakPoint';
import useCreateLessonQueryState from '../../../hooks/useCreateLessonQueryState';
import { useMetrics } from '../../../hooks/useMetrics';
import useRoutePrefetch from '../../../hooks/useRoutePrefetch';
import useRouterMatch from '../../../hooks/useRouterMatch';
import { useTestPrepState } from '../../../hooks/useTestPrepState';
import { ById } from '../../../services/backendService/types';
import {
  ContentCategory,
  lessonCategories,
  LessonCategory,
  mobileOrderLessonCategories,
  SubjectOption,
  subjectOptionMapper,
} from '../../../utils/createLessonPageUtils';
import { Button } from '../../_atoms/Button';
import { CustomIntercomButton } from '../../_atoms/CustomIntercomButton';
import { HideNextJsLoader } from '../../_atoms/HideNextJsLoader';
import { IconWrapper } from '../../_atoms/IconWrapper';
import { AnyContentOrSkillsTab, gradeOptions } from './AnyContentOrSkillTab';
import { CategoryList } from './CategorySelector';
import { CurifyTab } from './CurifyTab';
import { FindCategorizedLessonsTab } from './FindCategorizedLessonsTab';
import { MobileCategoryList } from './MobileCategoryList';
import { TestPrepTab } from './TestPrepTab';
import { CreateWithAIFormState } from './types';

export const SubPageLayout = ({
  children,
  pageTitle,
  subTitle,
}: {
  children: JSX.Element | JSX.Element[];
  pageTitle?: string;
  subTitle?: string;
}) => {
  const isDesktop = useBreakPoint(700);
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minHeight: '100%',
      }}
    >
      <div style={{ marginBottom: isDesktop ? 0 : 0 }}>
        {pageTitle && (
          <h1
            style={{
              width: '100%',
              textAlign: 'center',
              fontWeight: 800,
              fontSize: isDesktop ? 36 : 22,
              padding: '0 10px',
              marginBottom: 0,
            }}
          >
            {pageTitle}
          </h1>
        )}

        {subTitle && <h2 style={{ textAlign: 'center' }}>{subTitle}</h2>}
      </div>

      {children}
    </div>
  );
};

const LessonCategoryPicker = ({
  handleSelectCategory,
  presetState,
  setFormInputs,
  handleGeneratorUrlSearchChange,
  allRecentPrompts,
  allRecentPromptsVisible,
  setAllRecentPromptsVisible,
}: {
  handleSelectCategory: (categoryId: string) => void;
  presetState: string | null | undefined;
  setFormInputs: (d: CreateWithAIFormState) => void;
  handleGeneratorUrlSearchChange: ({
    subjectToggled,
    gradeToggled,
    categoryToggled,
    lessonCategoryToggled,
    newSearchPhrase,
    recentPromptIdToggled,
  }: {
    subjectToggled?: string | undefined;
    gradeToggled?: string | undefined;
    categoryToggled?: string | undefined;
    lessonCategoryToggled?: string | undefined;
    newSearchPhrase?: string | undefined;
    recentPromptIdToggled?: string | undefined;
  }) => void;
  allRecentPrompts: SavedRecentPrompt[];
  allRecentPromptsVisible: boolean;
  setAllRecentPromptsVisible: (d: boolean) => void;
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null); // Track the hovered card
  const metrics = useMetrics();
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const tenantSubjects = userAccessBadge?.user.profileInfo?.subjects;
  const tenantGrades = userAccessBadge?.user.profileInfo?.grades;

  const recentPromptsToDisplay = useMemo(() => {
    return allRecentPrompts.slice(
      0,
      allRecentPromptsVisible ? allRecentPrompts.length : 3,
    );
  }, [allRecentPrompts, allRecentPromptsVisible]);

  return (
    <SubPageLayout pageTitle="Create with AI" subTitle="How do you want to get started?">
      <CardsContainer>
        {Object.entries(lessonCategories).map(([id, lessonCategory], i) => (
          <Card
            key={id}
            variants={cardVariants(hoveredIndex)}
            custom={i}
            initial="initial"
            animate="animate"
            whileHover="hover" // Activate hover state
            onMouseEnter={() => setHoveredIndex(i)} // Set the hovered card
            onMouseLeave={() => setHoveredIndex(null)} // Reset on hover end
          >
            <CreateWithAIFloatingCard
              key={id}
              imgSrc={lessonCategory.img}
              title={lessonCategory.title}
              description={lessonCategory.description}
              onClick={() => {
                metrics.logEvent('CreatePage.DesktopLessonCategoryPicker.Clicked', {
                  selectedCategory: id,
                  schoolState: presetState,
                  tenantSubjects,
                  tenantGrades,
                });
                handleSelectCategory(id);
              }}
              isHovered={hoveredIndex === i}
              presetState={presetState}
            ></CreateWithAIFloatingCard>
          </Card>
        ))}
      </CardsContainer>
      {recentPromptsToDisplay?.length ? (
        <RecentPromptContainer>
          <h2
            style={{
              textAlign: 'center',
              fontWeight: 800,
              fontSize: '20px',
            }}
          >
            Your recent prompts
          </h2>
          {recentPromptsToDisplay.map((eachPromptData, i) => (
            <RecentPromptCard
              key={i}
              onClick={() => {
                metrics.logEvent('recent.prompt.selected', {
                  ...eachPromptData,
                  clickedAt: new Date(),
                });

                handleGeneratorUrlSearchChange({
                  lessonCategoryToggled: eachPromptData?.queryData?.lessonCategory,
                  categoryToggled: eachPromptData?.queryData?.categories,
                  subjectToggled: eachPromptData?.queryData?.subjects,
                  recentPromptIdToggled: eachPromptData?.id,
                });
                setFormInputs({
                  ...eachPromptData?.formInputs,
                });
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '5px',
                  width: `calc(100% - 24px)`,
                }}
              >
                <PromptTitle>
                  <Tooltip
                    mouseEnterDelay={1}
                    title={`${
                      eachPromptData?.formInputs?.uploadedFile?.name
                        ? `${eachPromptData?.formInputs?.uploadedFile?.name} | `
                        : ''
                    }${eachPromptData?.formInputs?.prompt}`}
                  >
                    {eachPromptData?.formInputs?.uploadedFile?.name
                      ? `${eachPromptData?.formInputs?.uploadedFile?.name} | `
                      : null}
                    {eachPromptData?.formInputs?.prompt}
                  </Tooltip>
                </PromptTitle>

                <RecentPromptDetails>
                  <span>
                    {dayjs(eachPromptData.createdAt).fromNow(true)}{' '}
                    {dayjs(eachPromptData.createdAt).fromNow(true) === 'just now'
                      ? ''
                      : ' ago'}
                  </span>

                  <span
                    style={{
                      color: '#D9D9D9',
                    }}
                  >
                    •
                  </span>

                  <span>
                    {
                      gradeOptions.find(
                        grade => grade.value === eachPromptData?.formInputs?.grade,
                      )?.displayName
                    }
                  </span>

                  {eachPromptData?.formInputs?.uploadedFile?.uploadedFileId ? null : (
                    <>
                      <span
                        style={{
                          color: '#D9D9D9',
                        }}
                      >
                        •
                      </span>

                      <span>
                        {eachPromptData?.formInputs?.subject &&
                        subjectOptionMapper?.[eachPromptData?.formInputs?.subject]
                          ?.displayName
                          ? subjectOptionMapper?.[eachPromptData?.formInputs?.subject]
                              ?.displayName
                          : 'Advance prompt'}
                      </span>
                    </>
                  )}
                </RecentPromptDetails>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <IconWrapper color={theme.colors.black} height={'24px'} width={'24px'}>
                  <ArrowRightSvg />
                </IconWrapper>
              </div>
            </RecentPromptCard>
          ))}

          {!allRecentPromptsVisible && allRecentPrompts?.length > 3 && (
            <RecentPromptCard
              style={{
                justifyContent: 'center',
                alignItems: 'center',
              }}
              onClick={() => {
                setAllRecentPromptsVisible(true);
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '10px',
                  color: '#5B5B5A',
                  fontSize: '16px',
                  fontWeight: 700,
                }}
              >
                <span>
                  <IconWrapper height="16px" width="16px">
                    <PlusSvg />
                  </IconWrapper>
                </span>
                <span
                  style={{
                    color: '#5B5B5A',
                    fontSize: '16px',
                    fontWeight: 700,
                  }}
                >
                  Load more
                </span>
              </div>
            </RecentPromptCard>
          )}
        </RecentPromptContainer>
      ) : (
        <></>
      )}
    </SubPageLayout>
  );
};
const MobileLessonCategoryPicker = ({
  handleSelectCategory,
  presetState,
  setFormInputs,
  handleGeneratorUrlSearchChange,
  allRecentPrompts,
  allRecentPromptsVisible,
  setAllRecentPromptsVisible,
}: {
  handleSelectCategory: (categoryId: string) => void;
  presetState: string | null | undefined;
  setFormInputs: (d: CreateWithAIFormState) => void;
  handleGeneratorUrlSearchChange: ({
    subjectToggled,
    gradeToggled,
    categoryToggled,
    lessonCategoryToggled,
    newSearchPhrase,
    recentPromptIdToggled,
  }: {
    subjectToggled?: string | undefined;
    gradeToggled?: string | undefined;
    categoryToggled?: string | undefined;
    lessonCategoryToggled?: string | undefined;
    newSearchPhrase?: string | undefined;
    recentPromptIdToggled?: string | undefined;
  }) => void;
  allRecentPrompts: SavedRecentPrompt[];
  allRecentPromptsVisible: boolean;
  setAllRecentPromptsVisible: (d: boolean) => void;
}) => {
  const metrics = useMetrics();
  const { accessBadge: userAccessBadge } = useAccessBadge();

  const tenantSubjects = userAccessBadge?.user.profileInfo?.subjects;
  const tenantGrades = userAccessBadge?.user.profileInfo?.grades;
  //order categories by the mobileOrderLessonCategories
  const orderedLessonCategories: ById<LessonCategory> = mobileOrderLessonCategories.reduce(
    (acc, id) => {
      const lessonCategory = lessonCategories[id];
      if (lessonCategory) {
        acc[id] = lessonCategory; // Add the lesson category to the accumulator object
      }
      return acc;
    },
    {} as ById<LessonCategory>,
  );

  const recentPromptsToDisplay = useMemo(() => {
    return allRecentPrompts.slice(
      0,
      allRecentPromptsVisible ? allRecentPrompts.length : 3,
    );
  }, [allRecentPrompts, allRecentPromptsVisible]);

  return (
    <SubPageLayout pageTitle="How do you want to get started?">
      <MobileCardsContainer>
        {Object.entries(orderedLessonCategories).map(([id, lessonCategory]) => (
          <MobileCreateWithAIFloatingCard
            key={id}
            title={lessonCategory.title}
            description={lessonCategory.description}
            onClick={() => {
              metrics.logEvent('CreatePage.MobileLessonCategoryPicker.Clicked', {
                selectedCategory: id,
                schoolState: presetState,
                tenantSubjects,
                tenantGrades,
              });
              handleSelectCategory(id);
            }}
            color={lessonCategory.color}
            presetState={presetState}
          />
        ))}
      </MobileCardsContainer>

      {recentPromptsToDisplay?.length ? (
        <RecentPromptContainer>
          <h2
            style={{
              textAlign: 'center',
              fontWeight: 800,
              fontSize: '20px',
            }}
          >
            Your recent prompts
          </h2>
          {recentPromptsToDisplay.map((eachPromptData, i) => (
            <RecentPromptCardMobile
              key={i}
              onClick={() => {
                metrics.logEvent('recent.prompt.selected', {
                  ...eachPromptData,
                  clickedAt: new Date(),
                  mobile: true,
                });

                handleGeneratorUrlSearchChange({
                  lessonCategoryToggled: eachPromptData?.queryData?.lessonCategory,
                  categoryToggled: eachPromptData?.queryData?.categories,
                  subjectToggled: eachPromptData?.queryData?.subjects,
                  recentPromptIdToggled: eachPromptData?.id,
                });
                setFormInputs({
                  ...eachPromptData?.formInputs,
                });
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '5px',
                  width: `calc(100% - 24px)`,
                }}
              >
                <PromptTitle>
                  <Tooltip
                    mouseEnterDelay={1}
                    title={`${
                      eachPromptData?.formInputs?.uploadedFile?.name
                        ? `${eachPromptData?.formInputs?.uploadedFile?.name} | `
                        : ''
                    } ${eachPromptData?.formInputs?.prompt}`}
                  >
                    {eachPromptData?.formInputs?.uploadedFile?.name
                      ? `${eachPromptData?.formInputs?.uploadedFile?.name} | `
                      : null}
                    {eachPromptData?.formInputs?.prompt}
                  </Tooltip>
                </PromptTitle>

                <RecentPromptDetails>
                  <span>
                    {dayjs(eachPromptData.createdAt).fromNow(true)}{' '}
                    {dayjs(eachPromptData.createdAt).fromNow(true) === 'just now'
                      ? ''
                      : ' ago'}
                  </span>

                  <span
                    style={{
                      color: '#D9D9D9',
                    }}
                  >
                    •
                  </span>

                  <span>
                    {
                      gradeOptions.find(
                        grade => grade.value === eachPromptData?.formInputs?.grade,
                      )?.displayName
                    }
                  </span>

                  {eachPromptData?.formInputs?.uploadedFile?.uploadedFileId ? null : (
                    <>
                      <span
                        style={{
                          color: '#D9D9D9',
                        }}
                      >
                        •
                      </span>

                      <span>
                        {eachPromptData?.formInputs?.subject &&
                        subjectOptionMapper?.[eachPromptData?.formInputs?.subject]
                          ?.displayName
                          ? subjectOptionMapper?.[eachPromptData?.formInputs?.subject]
                              ?.displayName
                          : 'Advance prompt'}
                      </span>
                    </>
                  )}
                </RecentPromptDetails>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <IconWrapper color={theme.colors.black} height={'24px'} width={'24px'}>
                  <ArrowRightSvg />
                </IconWrapper>
              </div>
            </RecentPromptCardMobile>
          ))}

          {!allRecentPromptsVisible && allRecentPrompts?.length > 3 && (
            <RecentPromptCard
              style={{
                justifyContent: 'center',
                alignItems: 'center',
              }}
              onClick={() => {
                setAllRecentPromptsVisible(true);
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '10px',
                  color: '#5B5B5A',
                  fontSize: '16px',
                  fontWeight: 700,
                }}
              >
                <span>
                  <IconWrapper height="16px" width="16px">
                    <PlusSvg />
                  </IconWrapper>
                </span>
                <span
                  style={{
                    color: '#5B5B5A',
                    fontSize: '16px',
                    fontWeight: 700,
                  }}
                >
                  Load more
                </span>
              </div>
            </RecentPromptCard>
          )}
        </RecentPromptContainer>
      ) : (
        <></>
      )}
    </SubPageLayout>
  );
};

const Card = styled(motion.div)`
  width: 250px;
  height: 270px;
  background-color: white;
  border-radius: 20px;
  position: relative;
  cursor: pointer;
`;

const cardVariants = (hoveredIndex: number | null) => {
  const getScale = (index: number) => {
    const centerIndex = 2; // The index of the center card (there are 5 cards)
    const distanceFromCenter = Math.abs(centerIndex - index);
    return 1 - distanceFromCenter * 0.1;
  };

  return {
    initial: (index: number) => ({
      scale: getScale(index),
      left: 0,
      opacity: 1,
      zIndex: 5,
      transition: { duration: 0.2 },
      boxShadow: theme.effects.magicFloatingShadow,
      //transform origin based on placement of card - center card centered and others to the left or right
      transformOrigin: index === 0 ? 'right' : index === 4 ? 'left' : 'center',
      y: 0,
    }),
    hover: (index: number) => ({
      scale: hoveredIndex === index ? 1.05 : 1,
      zIndex: hoveredIndex === index ? 10 : 5,
      boxShadow: theme.effects.magicFloatingShadowHovered,
      transition: { duration: 0.2, ease: 'linear', bounce: 0, stiffness: 300 },
      //make the card look like it is being lifted up
      y: -10,
    }),
  };
};

const CardsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 auto;
  height: 270px;
  width: 90%;
  max-width: 1200px;

  @media (max-width: 900px) {
    width: 100%;
    padding: 10px;
  }
`;

const RecentPromptContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  margin: 60px auto;
  width: 60%;
  max-width: 1200px;
  gap: 10px;
  cursor: pointer;
  user-select: none;

  @media (max-width: 900px) {
    width: 90%;
    padding: 10px;
  }
`;

const RecentPromptCard = styled.div`
  display: flex;
  flex-direction: row;
  border-radius: 5px;
  padding: 16px;
  background: #fbf7f2;
  width: 100%;
  justify-content: space-between;
  align-items: center;

  box-shadow: 0px 1.6px 3.6px 0px #00000021;
  transition: all 0.3s;
  &:hover {
    box-shadow: 0px 0.3px 0.9px 0px #0000001c;
  }
`;

const RecentPromptCardMobile = styled.div`
  display: flex;
  flex-direction: row;
  border-radius: 5px;
  padding: 8px;
  background: #fbf7f2;
  width: 100%;
  justify-content: space-between;
  align-items: center;

  box-shadow: 0px 1.6px 3.6px 0px #00000021;
  transition: all 0.3s;
  &:hover {
    box-shadow: 0px 0.3px 0.9px 0px #0000001c;
  }
`;

const RecentPromptDetails = styled.div`
  display: flex;
  flex-direction: row;
  gap: 5px;
  color: #5b5b5a;
  font-size: 12px;
  font-weight: 700;
`;

const PromptTitle = styled.h1`
  font-weight: 700;
  font-size: 16px;
  color: #282f33;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 0;
`;

const MobileCardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  margin: 0 auto;
  gap: 10px;
  width: 90%;
`;

const CreateWithAIFloatingCard = ({
  imgSrc,
  title,
  description,
  onClick,
  isHovered,
  presetState,
}: {
  imgSrc: string;
  title: string;
  description: string;
  onClick: () => void;
  isHovered: boolean;
  presetState: string | null | undefined;
}) => {
  return (
    <FloatingCardContainer onClick={onClick}>
      <ImageSection>
        <Image src={imgSrc} alt="" />
      </ImageSection>
      <TextSection>
        <h3>{getCardTitle(title, presetState)}</h3>
        <p>{description}</p>

        <CardButton
          styles={{
            color: theme.colors.green,
            display: 'flex',
            flexDirection: 'row-reverse',
          }}
          $isHovered={isHovered}
          icon={
            <IconWrapper
              style={{}}
              color={isHovered ? theme.colors.green : theme.colors.black}
              height={'10px'}
              width={'30px'}
            >
              <SlimLeftArrow />
            </IconWrapper>
          }
          type="linkStyle"
        >
          Continue
        </CardButton>
      </TextSection>
    </FloatingCardContainer>
  );
};

const testPrepfromState = (schoolState: string | null | undefined) => {
  switch (schoolState) {
    case 'TX':
      return 'STAAR';
    case 'CA':
      return 'CAASPP';
    case 'GA':
      return 'GMAS';
    case 'NY':
      return 'NYS Exam Prep';
    case 'MI':
      return 'M-STEP';
    case 'CO':
      return 'CMAS';
    case 'AZ':
      return 'AASA';
    case 'OH':
      return 'OST';
    case 'WV':
      return 'WVGSA';
    case 'NC':
      return 'NC Test Prep';
    case 'PA':
      return 'PSSA';
    default:
      return 'testing';
  }
};

const getCardTitle = (title: string, schoolState: string | null | undefined) => {
  if (title !== 'Prepare for ') return title;
  if (!schoolState) return 'Prepare for testing';
  //get the correct test name for the users state

  return `Prepare for ${testPrepfromState(schoolState)}`;
};

const getTestIdFromState = (schoolState: string | null | undefined) => {
  switch (schoolState) {
    case 'TX':
      return 'staar';
    case 'CA':
      return 'california_sbac_caaspp';
    case 'GA':
      return 'georgia_gmas';
    case 'MI':
      return 'mstep';
    case 'NY':
      return 'nys_exam_prep';
    case 'CO':
      return 'colorado_cmas';
    case 'AZ':
      return 'arizona_aasa';
    case 'OH':
      return 'ohio_ost';
    case 'WV':
      return 'west_virginia_wvgsa';
    case 'NC':
      return 'north_carolina_test_prep';
    case 'PA':
      return 'pennsylvania_pssa';
    default:
      return 'staar';
  }
};

const MobileCreateWithAIFloatingCard = ({
  title,
  description,
  color,
  onClick,
  presetState,
}: {
  title: string;
  description: string;
  color: string;
  onClick: () => void;
  presetState: string | null | undefined;
}) => {
  return (
    <FloatingCardContainer onClick={onClick} $borderColor={color}>
      <TextSection>
        <h3>{getCardTitle(title, presetState)}</h3>
        <p>{description}</p>

        <CardButton
          styles={{
            color: theme.colors.green,
            display: 'flex',
            flexDirection: 'row-reverse',
          }}
          icon={
            <IconWrapper
              style={{}}
              color={theme.colors.black}
              height={'10px'}
              width={'30px'}
            >
              <SlimLeftArrow />
            </IconWrapper>
          }
          type="linkStyle"
        >
          Continue
        </CardButton>
      </TextSection>
    </FloatingCardContainer>
  );
};

const CardButton = styled(Button)<{ $isHovered?: boolean }>`
  width: 100%;
  display: flex;
  justify-content: end;
  align-items: center;
  text-decoration: none;
  padding: 5px 0;
  transition: all linear 0.3s;

  span {
    transition: opacity linear 0.3s;
    opacity: 0;
    font-size: 14px;
    text-decoration: none;
  }

  &:hover {
    span {
      text-decoration: none;
    }
  }

  ${({ $isHovered }) =>
    $isHovered &&
    css`
      span {
        opacity: 1;
        text-decoration: none;
      }
    `}
`;

const FloatingCardContainer = styled.div<{ $borderColor?: string }>`
  width: 100%;
  height: 270px;
  border-radius: 10px;
  box-shadow: ${theme.effects.magicFloatingShadow};
  background-color: ${theme.colors.paleBeige};
  overflow: hidden;
  display: grid;
  grid-template-rows: auto 1fr;
  grid-template-columns: 1fr;

  @media (max-width: 700px) {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    position: relative;
    overflow: hidden;
    padding-left: 10px;
    border-radius: 5px;

    &::after {
      position: absolute;
      top: 0;
      left: 0;
      content: '';
      width: 5px;
      height: 100%;
      background-color: ${({ $borderColor }) =>
        $borderColor ? $borderColor : theme.colors.paleBeige};
    }

    button {
      padding: 0;
    }
  }
`;

const ImageSection = styled.div`
  width: 100%;
  width: 100%;
  height: 0;
  padding-top: 56.25%;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
  position: absolute;
  top: 0;
  left: 0;
`;

const TextSection = styled.div`
  padding: 10px 5px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  h3 {
    font-size: 20px;
    font-weight: bold;
    margin-bottom: 0px;
  }
  p {
    display: flex;
    font-size: 12px;
    color: ${theme.colors.greyVeryDark};
    margin-bottom: 0px;
    flex-grow: 1;
  }
`;

type SavedRecentPrompt = {
  formInputs: CreateWithAIFormState;
  queryData: {
    lessonCategory: string;
    categories: string;
    subjects: string;
  };
  createdAt: string;
  id: string;
};

const CreatePage = ({
  hasSingleTestprepFormAccess,
}: {
  hasSingleTestprepFormAccess: boolean;
}) => {
  const { tenantId } = useRouterMatch();
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const { testPrepState, dispatch } = useTestPrepState(userAccessBadge);
  const { selectedPage } = testPrepState;
  const presetState = userAccessBadge?.organization?.region;
  const tenantSubjects = userAccessBadge?.user.profileInfo?.subjects;
  const metrics = useMetrics();
  // Prefetch a single generator page
  useRoutePrefetch(`/generators/[generatorName]/g/[generatorId]`);
  //Prefetch lesson preview page
  useRoutePrefetch(`/[tenantId]/lessons/[templateId]`);
  //TODO handle what folder was selected before going to this page to use when creating the lesson
  const isDesktop = useBreakPoint(700);
  const router = useRouter();
  const { update } = useIntercom();

  const initFromLocalStorage = () => {
    return typeof window !== 'undefined'
      ? localStorage.getItem('@curipod_v1_createWithAI-grade')
      : undefined;
  };
  const [lessonType, setLessonType] = useState<SubjectOption>();
  const [formInputs, setFormInputs] = useState<CreateWithAIFormState>({
    grade: initFromLocalStorage() || '6th',
    selectedCategory: undefined,
  });

  const [allRecentPromptsVisible, setAllRecentPromptsVisible] = useState(false);
  const [allRecentPrompts, setAllRecentPrompts] = useState<SavedRecentPrompt[]>([]);

  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        const fetchedRecentPrompts = JSON.parse(
          localStorage.getItem('@curipod_v1_recentPrompts') || '[]',
        );
        setAllRecentPrompts(fetchedRecentPrompts);
      }
    } catch (error) {
      console.error('Error fetching recent prompts', error);
    }
  }, [router.query]);

  useEffect(() => {
    update({
      hideDefaultLauncher: true,
    });
    return () => {
      update({
        hideDefaultLauncher: false,
      });
    };
  }, [update]);

  const {
    clearCategoriesFilters,
    clearSubjectFilters,
    clearSubjectAndCategoryFilters,
    handleGeneratorUrlSearchChange,
    clearFilters,
    searchState,
  } = useCreateLessonQueryState();

  const handleSelectLessonCategory = (categoryId: string) => {
    const autoSelectedTestCategory = getTestIdFromState(presetState);

    handleGeneratorUrlSearchChange({
      lessonCategoryToggled: categoryId as string,
      //if desktop set the default category for the lesson category selected
      ...(isDesktop && {
        categoryToggled:
          categoryId === 'test_prep'
            ? autoSelectedTestCategory
            : categoryId === 'writing_skills'
            ? 'five_min_writing'
            : categoryId === 'daily_routines'
            ? 'bell_ringers'
            : undefined,
      }),
    });
    if (categoryId === 'test_prep' && isDesktop) {
      setFormInputs({ ...formInputs, selectedCategory: autoSelectedTestCategory });
    }

    if (categoryId === 'writing_skills' && isDesktop) {
      setFormInputs({ ...formInputs, selectedCategory: 'five_min_writing' });
    }

    if (categoryId === 'daily_routines' && isDesktop) {
      setFormInputs({ ...formInputs, selectedCategory: 'bell_ringers' });
    }

    //clear formInputs and topic if the user selects a different form
    if (categoryId === 'full_lesson' || categoryId === 'upload') {
      setFormInputs({
        ...formInputs,
        selectedCategory: 'full_lesson',
        subject: undefined,
        prompt: undefined,
        selectedStandard: undefined,
        uploadedFile: undefined,
      });
    }
  };
  const handleSelectCategory = (categoryId: string) => {
    metrics.logEvent('CreatePage.SelectCategory.Clicked', {
      selectedCategory: categoryId,
      selectedLessonCategory: searchState.lessonCategory,
      schoolState: presetState,
      tenantSubjects,
    });
    handleGeneratorUrlSearchChange({
      categoryToggled: categoryId as string,
    });
  };
  const handleSelectSubject = (subject: string) => {
    metrics.logEvent('CreatePage.SelectSubject.Clicked', {
      selectedSubject: subject,
      selectedLessonCategory: searchState.lessonCategory,
      schoolState: presetState,
      tenantSubjects,
    });
    if (
      subject === 'ela' ||
      subject === 'math' ||
      subject === 'science' ||
      subject === 'socialStudies'
    ) {
      handleGeneratorUrlSearchChange({
        subjectToggled: subject as string,
      });
      return;
    }

    //for all other subjects we want to go to directly to the generate category
    handleGeneratorUrlSearchChange({
      subjectToggled: subject as string,
      categoryToggled: 'generate',
    });
  };

  const handleClearFilters = () => {
    if (router.query.recentPromptId) {
      clearFilters();
      return;
    }
    //clear prompt and selectedStandard from formInputs
    setFormInputs({ ...formInputs, prompt: undefined, selectedStandard: undefined });
    if (searchState?.lessonCategory[0] === 'test_prep') {
      //if testprep
      if (selectedPage === 1 && hasSingleTestprepFormAccess) {
        dispatch({ type: 'RESET' });
        clearFilters();
        return;
      }
      if (selectedPage === 2 && hasSingleTestprepFormAccess) {
        dispatch({ type: 'GO_BACK', payload: 1 });
        return;
      }
      if (selectedPage === 3 && hasSingleTestprepFormAccess) {
        dispatch({ type: 'GO_BACK', payload: 2 });
        return;
      }
      clearFilters();
      return;
    }
    const isLessonWithSubcategories =
      searchState.subjects?.[0] === 'ela' ||
      searchState.subjects?.[0] === 'socialStudies' ||
      searchState.subjects?.[0] === 'science' ||
      searchState.subjects?.[0] === 'math';
    const isGenerateCategory = searchState.categories?.[0] === 'generate';
    const isOnlyLessonCategory =
      searchState.lessonCategory?.length === 1 &&
      searchState.subjects?.length === 0 &&
      searchState.categories?.length === 0;
    if (isOnlyLessonCategory) {
      clearFilters();
      return;
    }

    if (!isLessonWithSubcategories) {
      if (
        (searchState?.lessonCategory[0] === 'writing_skills' ||
          searchState?.lessonCategory[0] === 'daily_routines') &&
        isDesktop
      ) {
        clearFilters();
        return;
      }
      clearSubjectAndCategoryFilters();
      return;
    }

    if (!lessonType) {
      // Clear the subject if no lesson type is selected
      clearSubjectFilters();
      return;
    }

    if (searchState.categories?.length === 0) {
      // Clear the lesson type if no category is selected
      setLessonType(undefined);
      return;
    }

    if (isGenerateCategory && searchState.subjects[0]) {
      // Check if lesson type has more details
      const hasMoreDetails = subjectOptionMapper[
        searchState.subjects[0]
      ].lessonTypes?.find(lesson => lesson.id === lessonType?.id)?.hasMoreDetails;

      if (!hasMoreDetails) {
        // Clear lesson type if no additional details
        setLessonType(undefined);
      }

      // Clear category filters
      clearCategoriesFilters();
      //also clear prompt from formInputs
      setFormInputs({ ...formInputs, prompt: undefined, selectedStandard: undefined });
    }
  };

  const hasCategories = searchState.categories.length === 0;

  const orderTestPrepCategories = (categories: ContentCategory[]) => {
    //ordered ACT first, then your state test then all other categories alphabetically
    const sortedCategories = categories.sort((a, b) => a.title.localeCompare(b.title));

    const actIndex = sortedCategories.findIndex(
      category => category.id === 'college_entrance_exams',
    );

    if (actIndex > -1) {
      const actCategory = sortedCategories.splice(actIndex, 1);
      sortedCategories.unshift(actCategory[0]);
    }

    //ensure SBAC is the second category
    const sbacIndex = sortedCategories.findIndex(category => category.id === 'sbac');

    if (sbacIndex > -1) {
      const sbacCategory = sortedCategories.splice(sbacIndex, 1);
      sortedCategories.splice(1, 0, sbacCategory[0]);
    }

    const stateTestIndex = sortedCategories.findIndex(
      category => category.title === testPrepfromState(presetState),
    );

    //ensure state category is in 3rd position
    if (stateTestIndex > -1) {
      const stateTestCategory = sortedCategories.splice(stateTestIndex, 1);
      sortedCategories.splice(2, 0, stateTestCategory[0]);
    }

    return sortedCategories;
  };

  return (
    <PageContainer>
      <CustomIntercomButton
        icon={
          <IconWrapper height="20px" width="20px">
            <QuestionMarkSvg />
          </IconWrapper>
        }
      />
      <HideNextJsLoader />
      <BackgroundContainer>
        <MagicBackground />
      </BackgroundContainer>
      <BackButtonContainer>
        {searchState.lessonCategory && searchState.lessonCategory.length === 0 ? (
          <Link href={`/${tenantId}/home`} passHref>
            <a>
              <BackButton
                onClick={() => {
                  metrics.logEvent('CreatePage.GoHome.Clicked', {
                    prompt: formInputs.prompt,
                    grade: formInputs.grade,
                    subject: formInputs.subject,
                    fileType: formInputs.uploadedFile?.fileType,
                    currentPage: searchState.lessonCategory,
                    selectedCategory: formInputs.selectedCategory,
                    schoolState: presetState,
                    tenantSubjects,
                  });
                }}
                type="primary"
                icon={<HomeFilledSvg />}
                styles={{
                  height: 42,
                  width: 110,
                }}
              >
                Home
              </BackButton>
            </a>
          </Link>
        ) : (
          <BackButton
            type="primary"
            styles={{
              padding: '10px 20px 10px 10px',
              height: 42,
              width: 110,
            }}
            onClick={() => {
              metrics.logEvent('CreatePage.GoBack.Clicked', {
                prompt: formInputs.prompt,
                grade: formInputs.grade,
                subject: formInputs.subject,
                fileType: formInputs.uploadedFile?.fileType,
                currentPage: searchState.lessonCategory,
                selectedCategory: formInputs.selectedCategory,
                schoolState: presetState,
                tenantSubjects,
              });
              handleClearFilters();
            }}
            icon={
              <IconWrapper height={'30px'} width={'30px'}>
                <ArrowLeftSvg />
              </IconWrapper>
            }
          >
            Back
          </BackButton>
        )}
      </BackButtonContainer>

      <MainContainer className={'scrollArea'}>
        {searchState.lessonCategory &&
          searchState.lessonCategory.includes('full_lesson') && (
            <AnyContentOrSkillsTab
              handleSelectCategory={handleSelectCategory}
              formInputs={formInputs}
              setFormInputs={setFormInputs}
              searchState={searchState}
              handleSelectSubject={handleSelectSubject}
              lessonType={lessonType}
              setLessonType={setLessonType}
            />
          )}
        {searchState.lessonCategory &&
          searchState.lessonCategory.includes('test_prep') &&
          lessonCategories['test_prep'].categories &&
          hasSingleTestprepFormAccess && (
            <TestPrepTab
              formInputs={formInputs}
              setFormInputs={setFormInputs}
              testPrepState={testPrepState}
              dispatch={dispatch}
            />
          )}
        {isDesktop && (
          <>
            {searchState.lessonCategory && searchState.lessonCategory.length === 0 && (
              <LessonCategoryPicker
                presetState={presetState}
                handleSelectCategory={handleSelectLessonCategory}
                setFormInputs={setFormInputs}
                handleGeneratorUrlSearchChange={handleGeneratorUrlSearchChange}
                allRecentPrompts={allRecentPrompts}
                allRecentPromptsVisible={allRecentPromptsVisible}
                setAllRecentPromptsVisible={setAllRecentPromptsVisible}
              />
            )}
            {searchState.lessonCategory &&
              searchState.lessonCategory.includes('upload') && (
                <CurifyTab formInputs={formInputs} setFormInputs={setFormInputs} />
              )}
            {searchState.lessonCategory &&
              searchState.lessonCategory.includes('test_prep') &&
              lessonCategories['test_prep'].categories &&
              !hasSingleTestprepFormAccess && (
                <SubPageLayout
                  pageTitle={'Get your "just right" lesson to prep for the test'}
                >
                  <CategoryListContainer>
                    <CategoryList
                      handleSelectCategory={(categoryId: string) => {
                        handleSelectCategory(categoryId);
                        setFormInputs({ ...formInputs, selectedCategory: categoryId });
                      }}
                      visibleCategories={orderTestPrepCategories(
                        lessonCategories['test_prep'].categories,
                      )}
                      activeCategoryId={searchState.categories[0]}
                    />
                  </CategoryListContainer>
                  <FindCategorizedLessonsTab
                    formInputs={formInputs}
                    selectedCategory={searchState.categories[0]}
                    lessonCategoryId="test_prep_made_fun"
                  />
                </SubPageLayout>
              )}
            {searchState.lessonCategory &&
              searchState.lessonCategory.includes('writing_skills') &&
              lessonCategories['writing_skills'].categories && (
                <SubPageLayout pageTitle={'What skill are you practicing / instructing?'}>
                  <CategoryListContainer>
                    <CategoryList
                      handleSelectCategory={(categoryId: string) => {
                        handleSelectCategory(categoryId);
                        setFormInputs({ ...formInputs, selectedCategory: categoryId });
                      }}
                      visibleCategories={lessonCategories['writing_skills'].categories}
                      activeCategoryId={searchState.categories[0]}
                    />
                  </CategoryListContainer>
                  <FindCategorizedLessonsTab
                    formInputs={formInputs}
                    lessonCategoryId={searchState.categories[0]}
                    selectedCategory={searchState.categories[0]}
                  />
                </SubPageLayout>
              )}
            {searchState.lessonCategory &&
              searchState.lessonCategory.includes('daily_routines') &&
              lessonCategories['daily_routines'].categories && (
                <SubPageLayout pageTitle={'Customize your daily routine lesson'}>
                  <CategoryListContainer>
                    <CategoryList
                      handleSelectCategory={(categoryId: string) => {
                        handleSelectCategory(categoryId);
                        setFormInputs({ ...formInputs, selectedCategory: categoryId });
                      }}
                      visibleCategories={lessonCategories['daily_routines'].categories}
                      activeCategoryId={searchState.categories[0]}
                    />
                  </CategoryListContainer>
                  <FindCategorizedLessonsTab
                    formInputs={formInputs}
                    lessonCategoryId="daily_routines"
                    selectedCategory={searchState.categories[0]}
                  />
                </SubPageLayout>
              )}
          </>
        )}
        {!isDesktop && (
          <>
            {searchState.lessonCategory && searchState.lessonCategory.length === 0 && (
              <MobileLessonCategoryPicker
                handleSelectCategory={handleSelectLessonCategory}
                presetState={presetState}
                setFormInputs={setFormInputs}
                handleGeneratorUrlSearchChange={handleGeneratorUrlSearchChange}
                allRecentPrompts={allRecentPrompts}
                allRecentPromptsVisible={allRecentPromptsVisible}
                setAllRecentPromptsVisible={setAllRecentPromptsVisible}
              />
            )}
            {searchState.lessonCategory &&
              searchState.lessonCategory.includes('upload') && (
                <CurifyTab formInputs={formInputs} setFormInputs={setFormInputs} />
              )}
            {searchState.lessonCategory &&
              searchState.lessonCategory.includes('test_prep') &&
              lessonCategories['test_prep'].categories &&
              !hasSingleTestprepFormAccess && (
                <>
                  {hasCategories ? (
                    //if there are no categories selected show the categories
                    <SubPageLayout
                      pageTitle={'Get your "just right" lesson to prep for the test'}
                    >
                      <MobileCategoryList
                        handleSelectCategory={(categoryId: string) => {
                          handleSelectCategory(categoryId);
                          setFormInputs({ ...formInputs, selectedCategory: categoryId });
                        }}
                        visibleCategories={lessonCategories['test_prep'].categories}
                      />
                    </SubPageLayout>
                  ) : (
                    <FindCategorizedLessonsTab
                      selectedCategory={searchState.categories[0]}
                      categoryTitle={
                        lessonCategories['test_prep'].categories?.find(
                          category => category.id === searchState.categories[0],
                        )?.title || ''
                      }
                      formInputs={formInputs}
                      lessonCategoryId="test_prep_made_fun"
                    />
                  )}
                </>
              )}
            {searchState.lessonCategory &&
              searchState.lessonCategory.includes('writing_skills') &&
              lessonCategories['writing_skills'].categories && (
                <>
                  {hasCategories ? (
                    //if there are no categories selected show the categories
                    <SubPageLayout pageTitle={lessonCategories['writing_skills'].title}>
                      <MobileCategoryList
                        handleSelectCategory={(categoryId: string) => {
                          handleSelectCategory(categoryId);
                          setFormInputs({ ...formInputs, selectedCategory: categoryId });
                        }}
                        visibleCategories={lessonCategories['writing_skills'].categories}
                      />
                    </SubPageLayout>
                  ) : (
                    <FindCategorizedLessonsTab
                      categoryTitle={
                        lessonCategories['writing_skills'].categories?.find(
                          category => category.id === searchState.categories[0],
                        )?.title || ''
                      }
                      formInputs={formInputs}
                      lessonCategoryId={searchState.categories[0]}
                      selectedCategory={searchState.categories[0]}
                    />
                  )}
                </>
              )}
            {searchState.lessonCategory &&
              searchState.lessonCategory.includes('daily_routines') &&
              lessonCategories['daily_routines'].categories && (
                <>
                  {hasCategories ? (
                    <SubPageLayout pageTitle={lessonCategories['daily_routines'].title}>
                      <MobileCategoryList
                        handleSelectCategory={(categoryId: string) => {
                          handleSelectCategory(categoryId);
                          setFormInputs({ ...formInputs, selectedCategory: categoryId });
                        }}
                        visibleCategories={lessonCategories['daily_routines'].categories}
                      />
                    </SubPageLayout>
                  ) : (
                    <FindCategorizedLessonsTab
                      categoryTitle={
                        lessonCategories['daily_routines'].categories?.find(
                          category => category.id === searchState.categories[0],
                        )?.title || ''
                      }
                      formInputs={formInputs}
                      lessonCategoryId="daily_routines"
                      selectedCategory={searchState.categories[0]}
                    />
                  )}
                </>
              )}
          </>
        )}
      </MainContainer>
    </PageContainer>
  );
};

const BackButton = styled(Button)`
  padding: 10px 20px;
  background-color: ${theme.colors.white};
  box-shadow: ${theme.effects.magicFloatingShadow};
  color: ${theme.colors.black};

  &:hover,
  &:focus,
  &:active {
    background-color: ${theme.colors.greyLight};
    color: ${theme.colors.black};
    box-shadow: ${theme.effects.defaultShadow};
  }
`;

const CategoryListContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  margin-bottom: 20px;
`;

const MainContainer = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding-top: 62px; // Just below the back button
`;
const BackButtonContainer = styled.div`
  position: absolute;
  top: 20px;
  margin-left: 20px;
  width: fit-content;
  z-index: 2;
`;

const BackgroundContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  svg {
    height: 98vh; // 99 and 100 adds a scroll...
    width: 100vw;
  }
`;

const PageContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  background: radial-gradient(
    145.25% 138.82% at 27.25% -63.82%,
    #ffc698 0%,
    #fff1e6 100%
  );
  /* background: radial-gradient(87.35% 87.34% at 38.44% -12.82%, #ffe5cf 0%, #fff1e6 100%); */
`;

export default CreatePage;
